import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';

// Cache for translations to avoid repeated API calls
const translationCache = new Map();

/**
 * Hook to automatically translate content based on current language
 * @param {string} text - The original text (usually in English)
 * @param {string} sourceLang - The source language code (default: 'en')
 * @param {string} targetLang - The target language code
 * @param {object} manualTranslations - Manual translations object { fr: "...", es: "...", de: "..." }
 * @returns {object} { translatedText, isLoading, error }
 */
export const useAutoTranslate = (text, sourceLang = 'en', targetLang = 'en', manualTranslations = null) => {
  const [translatedText, setTranslatedText] = useState(text);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const translate = async () => {
      // If no text or same language, return original
      if (!text || sourceLang === targetLang) {
        setTranslatedText(text || '');
        return;
      }

      // Check if manual translation exists
      if (manualTranslations && manualTranslations[targetLang]) {
        setTranslatedText(manualTranslations[targetLang]);
        return;
      }

      // Check local cache
      const cacheKey = `${sourceLang}:${targetLang}:${text}`;
      if (translationCache.has(cacheKey)) {
        setTranslatedText(translationCache.get(cacheKey));
        return;
      }

      // Translate via API
      setIsLoading(true);
      setError(null);

      try {
        const response = await axios.post(`${API}/translate`, {
          text,
          source_lang: sourceLang,
          target_lang: targetLang
        });

        const translated = response.data.translated_text;
        translationCache.set(cacheKey, translated);
        setTranslatedText(translated);
      } catch (err) {
        console.error('Translation error:', err);
        setError(err.message);
        setTranslatedText(text); // Fallback to original
      } finally {
        setIsLoading(false);
      }
    };

    translate();
  }, [text, sourceLang, targetLang, manualTranslations]);

  return { translatedText, isLoading, error };
};

/**
 * Translate multiple texts in a batch
 * @param {Array} items - Array of {key, text, sourceLang}
 * @param {string} targetLang - Target language
 * @returns {Promise<object>} - Object with translated texts keyed by their keys
 */
export const translateBatch = async (items, targetLang) => {
  if (!items || items.length === 0) return {};

  // Filter out items that don't need translation
  const needsTranslation = items.filter(item => 
    item.text && item.sourceLang !== targetLang
  );

  if (needsTranslation.length === 0) {
    return items.reduce((acc, item) => {
      acc[item.key] = item.text || '';
      return acc;
    }, {});
  }

  try {
    const response = await axios.post(`${API}/translate/batch`, {
      texts: needsTranslation.map(item => ({
        key: item.key,
        text: item.text,
        source_lang: item.sourceLang || 'en'
      })),
      target_lang: targetLang
    });

    // Merge with items that didn't need translation
    const results = { ...response.data.translations };
    items.forEach(item => {
      if (!results[item.key]) {
        results[item.key] = item.text || '';
      }
    });

    return results;
  } catch (error) {
    console.error('Batch translation error:', error);
    // Return original texts on error
    return items.reduce((acc, item) => {
      acc[item.key] = item.text || '';
      return acc;
    }, {});
  }
};

/**
 * Get translated content, preferring manual translations over auto-translate
 * @param {string} defaultValue - Default/original text
 * @param {object} translations - Manual translations object
 * @param {string} currentLang - Current language code
 * @returns {string} - The appropriate text for the current language
 */
export const getTranslatedContent = (defaultValue, translations, currentLang) => {
  if (!defaultValue) return '';
  if (currentLang === 'en' || !translations) return defaultValue;
  return translations[currentLang] || defaultValue;
};

export default useAutoTranslate;
